import * as React from "react";
import { RouteComponentProps } from "react-router";
import { DisplayMedium } from "@conserveit/blocks";
import { i18n } from "../../../i18n";
import { InfoBar } from "../InfoBar";
import { ToolBar } from "../ToolBar";
import { PageHeader } from "../../Layout/Page";
import { updateMonitoringUrlHistory } from "../../../redux/modules/urlHistory/actionCreators";
import { updateDaysRange } from "../../../redux/modules/_BETA/selections";
import { ExpandableGroup } from "../ExpandableGroup";
import { updateQueryString } from "../utils";
import {
  useQueryState,
  useExpandedState,
  useMonitoringState,
} from "../utils/hooks";
import { TimeRange, OnSearch, GroupBy, SortBy, Category } from "../utils/types";
import { TIME_RANGE_OPTIONS } from "../utils/options";
import {
  setPersisted,
  getPersisted,
  clearStorage,
} from "../../../utils/storage";
import { useActions } from "../../../context/analysis";
import { MONITORING_LS_KEY, EXPANDING_KEY } from "../utils/constants";

interface Props {
  category: Category;
  data: Insite.AppState["monitoring"];
  history: RouteComponentProps["history"];
  updateHistory: typeof updateMonitoringUrlHistory;
  updateTimeRange: typeof updateDaysRange;
  timeRange: Insite.AppState["betaSelections"]["daysRange"];
  showSnoozed: Insite.AppState["insights"]["showSnoozed"];
}

const onSearch: OnSearch =
  (setFiltered, setQuery) =>
  ({ filtered, query }) => {
    setFiltered(filtered);
    setQuery(query);
    setPersisted({ key: "Monitoring-search", prop: "query" }, query);
  };

const onTimeRangeChange =
  (
    updateTimeRange: typeof updateDaysRange,
    history: RouteComponentProps["history"]
  ) =>
  (days: TimeRange) => {
    updateQueryString(`${days}`, history, "daysRange");
    updateTimeRange(days);
  };

const onExpand =
  (
    expandAll: boolean,
    setExpandAll: React.Dispatch<React.SetStateAction<boolean>>
  ) =>
  () => {
    clearStorage({ key: EXPANDING_KEY.monitoring });
    setExpandAll(!expandAll);
  };

const onGroupChange =
  (setGroup: React.Dispatch<React.SetStateAction<GroupBy>>) =>
  (option: GroupBy) => {
    setGroup(option);
    setPersisted({ key: "Monitoring-groups", prop: "groupBy" }, option);
  };

const onSortChange =
  (setSort: React.Dispatch<React.SetStateAction<SortBy>>) =>
  (option: SortBy) => {
    setSort(option);
    setPersisted({ key: "Monitoring-groups", prop: "sortBy" }, option);
  };

export const Monitoring: React.FunctionComponent<Props> = ({
  category,
  data,
  history,
  timeRange,
  showSnoozed,
  updateHistory,
  updateTimeRange,
}) => {
  // Set the view mode to monitoring
  const { setViewMode } = useActions();
  setViewMode("monitoring");

  const firstRender = React.useRef(true);
  // Group open/close all state
  const { expandAll, setExpandAll } = useExpandedState(
    firstRender.current,
    "monitoring"
  );
  const initialSearch = getPersisted(
    { key: MONITORING_LS_KEY, prop: "query" },
    ""
  );

  // Data processing and group composition
  const {
    finalGrouped,
    counts,
    filtered,
    setFiltered,
    setGroup,
    setSort,
    composed,
    sort,
  } = useMonitoringState(data, category, showSnoozed, initialSearch !== "");
  const { setQuery } = useQueryState(history, updateHistory);

  // Map timerange label from props
  const timeRangeLabel = React.useMemo(() => {
    return TIME_RANGE_OPTIONS.monitoring
      .find((o) => o.value === timeRange)!
      .label(i18n);
  }, [timeRange]);

  return (
    <>
      <PageHeader>
        <DisplayMedium>
          <InfoBar
            insightsCount={counts.insightsCount}
            siteCount={counts.siteCount}
            equipCount={counts.equipCount}
            timeRange={timeRangeLabel}
          />
        </DisplayMedium>
      </PageHeader>
      <ToolBar
        searchData={composed}
        snoozedCount={counts.snoozedCount}
        currentGroup={finalGrouped.group}
        currentSort={sort}
        timeRange={timeRange}
        expandAll={expandAll}
        onExpand={onExpand(expandAll, setExpandAll)}
        onSearch={onSearch(setFiltered, setQuery)}
        searchSessionKey="monitoringSearch"
        initialSearch={initialSearch}
        onTimeRangeChange={onTimeRangeChange(updateTimeRange, history)}
        onGroupChange={onGroupChange(setGroup)}
        onSortChange={onSortChange(setSort)}
      />
      <ExpandableGroup
        finalGrouped={finalGrouped}
        showSnoozed={showSnoozed}
        expandAll={expandAll}
        filtered={filtered}
      />
    </>
  );
};
